import { Component, ViewChild, DestroyRef } from '@angular/core';
import { OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgSelectComponent } from '@ng-select/ng-select';
import { CohortsService } from '../../services/cohorts/cohorts.service';
import { SpecialtiesService } from '../../services/specialties/specialties.service';
import { AlertBoxComponent } from 'src/app/shared/components/alert-box/alert-box.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AddCohortExpertComponent } from '../../components/add-cohort-expert/add-cohort-expert.component';
import { CohortExpertData } from '../../models/cohort-expert.model';
import { ConfirmationBoxComponent } from 'src/app/shared/components/confirmation-box/confirmation-box.component';
import { extractErrorMessage } from 'src/app/core/services/uitls/error.util';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-cohort-details',
  standalone: false,
  templateUrl: './cohort-details.component.html',
  styleUrls: ['./cohort-details.component.scss'],
})
export class CohortDetailsComponent implements OnInit {
  title: string = 'تفاصيل المجموعة';
  cohortFormGroup: FormGroup = new FormGroup({});
  cohortId: string | null = null;
  initSpecializations: any[] = [];
  specializations: any[] = [];
  specializationsPage: number = 1;
  specializationsPageCount: number = 1;
  specializationsSearchInput!: string;
  experts: any[] = [];
  cohortLog: any[] = [];
  cohortDetails: any;
  saveDialogRef: MatDialogRef<AlertBoxComponent> | undefined;
  deleteDialogRef: MatDialogRef<ConfirmationBoxComponent> | undefined;
  addExpertDialogRef: MatDialogRef<any> | undefined;

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private formBuilder = inject(FormBuilder);
  private cohortService = inject(CohortsService);
  private specialtiesService = inject(SpecialtiesService);
  private dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);

  @ViewChild('selectSpecializations') selectSpecializations!: NgSelectComponent;
  specializationsSearchTimeout: ReturnType<typeof setTimeout> | undefined;

  ngOnInit(): void {
    this.createCohortFormGroup();
    this.getSpecializations();
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.cohortId = params.get('id');
      if (this.cohortId === 'add') {
        this.title = 'إضافة مجموعة';
      } else {
        this.title = 'تفاصيل المجموعة';
        this.getCohortDetails(this.cohortId || '');
        this.getCohortExperts(this.cohortId || '');
        this.getCohortLog(this.cohortId || '');
      }
    });
  }

  createCohortFormGroup(): void {
    this.cohortFormGroup = this.formBuilder.group({
      name: ['', Validators.required],
      description: ['', Validators.required],
      specialization_ids: [[], Validators.required]
    });
  }

  getCohortDetails(cohortId: string): void {
    this.cohortService.getCohortById(Number(cohortId)).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.cohortDetails = response.data;
        this.initSpecializations = this.cohortDetails.specializations?.map((spec: any) => spec.id) || [];
        this.cohortFormGroup.patchValue({
          name: this.cohortDetails.name,
          description: this.cohortDetails.description,
          specialization_ids: this.cohortDetails.specializations?.map((spec: any) => spec.id)
        });
      }
    );
  }

  getCohortExperts(cohortId: string): void {
    this.cohortService.getCohortExperts(Number(cohortId)).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.experts = response.data;
      }
    );
  }

  getCohortLog(cohortId: string): void {
    this.cohortService.getCohortLog(Number(cohortId)).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.cohortLog = response.data;
      }
    );
  }

  getSpecializations(): void {
    this.specialtiesService.getSpecialties({ page: this.specializationsPage, query: this.specializationsSearchInput }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        const newSpecializations = response.data.filter(
          (newItem: any) => !this.specializations.some(existing => existing.id === newItem.id)
        );
        this.specializations = [...this.specializations, ...newSpecializations];
        this.specializationsPageCount = response.meta?.pagination?.pages_count || 1;
      }
    );
  }

  setSpecializations(event: any): void {
    this.initSpecializations = event;
    this.cohortFormGroup.controls.specialization_ids.patchValue([...event]);
  }

  onScrollSpecializationsPagination(event: any): void {
    if (event && this.specializationsPage < this.specializationsPageCount) {
      this.specializationsPage++;
      this.getSpecializations();
    }
  }

  updateSpecializations(event: any) {
    if (event.reset) {
      this.specializationsPage = 1;
      this.specializationsSearchInput = '';
    } else {
      this.specializationsPage = event.page ? event.page : this.specializationsPage;
      this.specializationsSearchInput = event?.searchInput;
    }
    this.getSpecializations();
  }

  addExpert(): void {
    const data: CohortExpertData = {
      specializations: this.cohortDetails?.specializations,
      specialization_ids: [],
      expert_ids: [],
      initExperts: [],
      isEditMode: false
    };
    this.openAddExpertDialog(data);
  }

  openAddExpertDialog(data: CohortExpertData): void {
    this.addExpertDialogRef = this.dialog.open(AddCohortExpertComponent, {
      data: data,
      panelClass: 'add-cohort-expert-container',
    });
    this.addExpertDialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result: any) => {
      if (result) {
        const newExperts: { expert_id: number; specialization_ids: number[] }[] = [];
        result.expert_ids.forEach((expertId: number) => {
          newExperts.push({ expert_id: expertId, specialization_ids: result.specialization_ids });
        });
        const expertsPayload = {
          experts: newExperts
        }
        this.updateCohort(expertsPayload, { buttonId: 'add-expert-button' }, true);
      }
    });
  }

  editExpert(expert: any): void {
    const data: CohortExpertData = {
      specializations: this.cohortDetails?.specializations,
      specialization_ids: [expert.specializations?.map((spec: any) => spec.id)].flat(),
      expert_ids: [],
      initExperts: [expert.expert?.id],
      isEditMode: true
    };
    this.openAddExpertDialog(data);
  }

  deleteExpert(expertId: any, index: number): void {
    this.showDeleteConfirmationBox(expertId, index);
  }

  goBack(): void {
    this.router.navigate(['/pages/ask-ayen/cohorts']);
  }

  save(event: any): void {
    if (this.cohortFormGroup.invalid) {
      this.cohortFormGroup.markAllAsTouched();
      return;
    }

    const cohortData = this.cohortFormGroup.value;
    if (this.cohortId === 'add') {
      this.createCohort(cohortData, event);
    } else {
      this.updateCohort(cohortData, event);
    }
  }

  createCohort(cohortData: any, event: any): void {
    this.cohortService.createCohort(cohortData, event.buttonId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.showAlertBox(true, 'تم إنشاء المجموعة بنجاح');
        this.router.navigate(['/pages/ask-ayen/cohorts', response.data.id], { replaceUrl: true });
      },
      (error: any) => {
        this.showAlertBox(false, extractErrorMessage(error) || 'حدث خطأ أثناء إنشاء المجموعة');
      }
    );
  }

  updateCohort(cohortData: any, event: any, updateExperts: boolean = false): void {
    this.cohortService.updateCohort(Number(this.cohortId), cohortData, event.buttonId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (updateExperts) {
          this.getCohortExperts(this.cohortId || '');
          this.getCohortLog(this.cohortId || '');
        } else {
          this.showAlertBox(true, 'تم تعديل المجموعة بنجاح');
        }
      },
      (error: any) => {
        this.showAlertBox(false, extractErrorMessage(error) || 'حدث خطأ أثناء تعديل المجموعة');
      }
    );
  }

  showAlertBox(success: boolean, message: string) {
    this.saveDialogRef = this.dialog.open(AlertBoxComponent, {
      data: {
        imgSuccess: success,
        message: message,
        button: 'تم'
      },
    });
  }

  showDeleteConfirmationBox(id: number, index: number) {
    this.deleteDialogRef = this.dialog.open(ConfirmationBoxComponent, {
      data: {
        id,
        index,
        message: `هل أنت متأكد من الحذف؟`
      }
    });

    this.deleteDialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(result => {
      if (result) {
        this.cohortService.deleteCohortExpert(Number(this.cohortId), id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            this.experts.splice(index, 1);
            this.showAlertBox(true, 'تم حذف الخبير بنجاح');
          },
          error: () => {
            this.showAlertBox(false, 'عذراً لا يمكن حذف الخبير');
          }
        });
      }
    });
  }
}
